"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

import { useEffect, useMemo, useState } from "react";

import { useClerk, useUser } from "@clerk/nextjs";
import Lottie from "@lottielab/lottie-player/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  Folder,
  Globe,
  History,
  Loader2,
  LogOut,
  MessageSquareWarning,
  MoreHorizontal,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Search,
  Settings,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useMotionValueEvent, useScroll } from "motion/react";
import posthog from "posthog-js";
import { toast } from "sonner";

import { ConnectDialog } from "@/frontend/components/shared/connect-dialog";
import { GetExtraDialog } from "@/frontend/components/shared/get-extra-dialog";
import { SearchDialog } from "@/frontend/components/shared/search-dialog";
import { SettingsDialog } from "@/frontend/components/shared/settings-dialog";
import { UpgradeDialog } from "@/frontend/components/shared/upgrade-dialog";
import { Button } from "@/frontend/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/frontend/components/ui/collapsible";
import { CommandShortcut } from "@/frontend/components/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/frontend/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/frontend/components/ui/dropdown-menu";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/frontend/components/ui/sidebar";
import { Textarea } from "@/frontend/components/ui/textarea";
import { useDeleteJam, useJamsQuery } from "@/frontend/hooks/jam";
import {
  useCreateProject,
  useDeleteProject,
  useProjectsQuery,
} from "@/frontend/hooks/project";
import { usePlanQuery } from "@/frontend/hooks/subscription";
import { getErrorMessage } from "@/frontend/lib/error-utils";
import { PLANS } from "@/frontend/lib/plans";
import { useSidebarStore } from "@/frontend/lib/stores";
import { cn } from "@/frontend/lib/utils";
import { Plan } from "@/shared/types";

function SidebarContent_() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { state, toggleSidebar } = useSidebar();
  const router = useRouter();
  const queryClient = useQueryClient();

  const {
    setIsSettingsDialogOpen,
    setIsSearchDialogOpen,
    setIsUpgradeDialogOpen,
    setIsGetExtraDialogOpen,
  } = useSidebarStore();

  const getPlanQuery = usePlanQuery();
  const getProjectsQuery = useProjectsQuery();
  const getJamsQuery = useJamsQuery(8);

  const [isJamsExpanded, setIsJamsExpanded] = useState(true);
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(true);
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");
  const [newProjectInstructions, setNewProjectInstructions] = useState("");
  const [isLogoPlaying, setIsLogoPlaying] = useState(false);
  const [shouldResetLogo, setShouldResetLogo] = useState(false);

  const deleteProjectMutation = useDeleteProject();
  const createProjectMutation = useCreateProject();

  const handleCreateProject = () => {
    if (!newProjectName.trim()) {
      toast.error("Project name is required");
      return;
    }

    createProjectMutation.mutate(
      {
        name: newProjectName,
        description: newProjectDescription || undefined,
        instructions: newProjectInstructions || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Project created successfully");
          setIsCreateProjectOpen(false);
          setNewProjectName("");
          setNewProjectDescription("");
          setNewProjectInstructions("");
        },
        onError: (error) => {
          toast.error(getErrorMessage(error, "Failed to create project"));
        },
      },
    );
  };

  const deleteJamMutation = useDeleteJam();

  const plan = getPlanQuery.data ? PLANS[getPlanQuery.data as Plan] : null;

  const jams = useMemo(
    () =>
      (
        getJamsQuery.data?.pages.flatMap((page) => (page ? page.jams : [])) ??
        []
      ).slice(0, 10),
    [getJamsQuery.data?.pages],
  );
  const hasMoreJams = getJamsQuery.hasNextPage;
  const projects = getProjectsQuery.data ?? [];

  const isCollapsed = state === "collapsed";

  return (
    <>
      <SidebarHeader className="items-start">
        <Link
          href="/"
          className="flex items-center justify-center p-2"
          onMouseEnter={() => {
            setIsLogoPlaying(true);
            setShouldResetLogo(false);
          }}
          onMouseLeave={() => {
            setIsLogoPlaying(false);
            setShouldResetLogo(true);
          }}
        >
          <Lottie
            className="size-6"
            src="https://cdn.lottielab.com/l/6H7EEEBrHF5oR7.json"
            playing={isLogoPlaying}
            {...(shouldResetLogo && { time: 0 })}
          />
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => setIsSearchDialogOpen(true)}
                tooltip="Search ⌘K"
              >
                <Search />
                <span>Search</span>
                <CommandShortcut>⌘K</CommandShortcut>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="New jam">
                <Link href="/">
                  <Plus />
                  <span>New jam</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Apps">
                <Link href="/apps">
                  <Globe />
                  <span>Apps</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <Collapsible
              open={projects.length === 0 ? false : isProjectsExpanded}
              onOpenChange={(value) => {
                if (projects.length > 0) {
                  setIsProjectsExpanded(value);
                }
              }}
              asChild
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <div className="group/projects relative">
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip="Projects"
                      onClick={(e) => {
                        if (projects.length === 0) {
                          e.preventDefault();
                          router.push("/projects");
                        } else if (isCollapsed) {
                          e.preventDefault();
                          setIsProjectsExpanded(true);
                          toggleSidebar();
                        }
                      }}
                    >
                      <div className="relative flex h-4 w-4 shrink-0 items-center justify-center">
                        {getProjectsQuery.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Folder
                              className={cn(
                                "absolute inset-0 h-4 w-4 transition-opacity duration-200",
                                !isCollapsed &&
                                  projects.length > 0 &&
                                  "group-hover/projects:opacity-0",
                              )}
                            />
                            {projects.length > 0 && (
                              <ChevronRight
                                className={cn(
                                  "absolute inset-0 h-4 w-4 transition-all duration-200 group-data-[state=open]/collapsible:rotate-90",
                                  isCollapsed
                                    ? "scale-0 opacity-0"
                                    : "scale-100 opacity-0 group-hover/projects:opacity-100",
                                )}
                              />
                            )}
                          </>
                        )}
                      </div>
                      <span>Projects</span>
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  {!isCollapsed && projects.length > 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "absolute top-1/2 right-1 z-10 h-6 w-6 -translate-y-1/2 opacity-0 transition-opacity duration-200 group-hover/projects:opacity-100",
                        "flex items-center justify-center hover:bg-accent hover:text-accent-foreground",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsCreateProjectOpen(true);
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {projects.map((project) => (
                      <SidebarMenuSubItem
                        key={project.id}
                        className="group/item relative overflow-hidden"
                      >
                        <SidebarMenuSubButton asChild>
                          <Link
                            href={`/project/${project.id}`}
                            className="overflow-hidden"
                          >
                            <span
                              className="inline-block max-w-full overflow-hidden pr-8 text-nowrap [mask-image:linear-gradient(to_right,black_85%,transparent_100%)] group-hover/item:[mask-image:linear-gradient(to_right,black_70%,transparent_90%)]"
                              style={{
                                textOverflow: "clip",
                              }}
                            >
                              {project.name}
                            </span>
                          </Link>
                        </SidebarMenuSubButton>
                        {!isCollapsed && (
                          <Dialog>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "absolute top-1/2 right-1 h-6 w-6 -translate-y-1/2 opacity-0 transition-opacity duration-200 group-hover/item:opacity-100",
                                    "flex items-center justify-center hover:bg-accent hover:text-accent-foreground",
                                  )}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent side="right" align="start">
                                <DialogTrigger asChild>
                                  <DropdownMenuItem
                                    className="text-destructive hover:text-destructive"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Trash2 />
                                    Delete
                                  </DropdownMenuItem>
                                </DialogTrigger>
                              </DropdownMenuContent>
                            </DropdownMenu>

                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle>Delete project</DialogTitle>
                              </DialogHeader>
                              <p className="text-sm text-muted-foreground">
                                Are you sure you want to delete{" "}
                                <span className="font-semibold text-muted">
                                  {project.name}
                                </span>
                                ? This action cannot be undone and will
                                permanently delete all files and jams associated
                                with this project.
                              </p>
                              <DialogFooter>
                                <DialogClose asChild>
                                  <Button
                                    variant="outline"
                                    className="text-foreground"
                                  >
                                    Cancel
                                  </Button>
                                </DialogClose>
                                <Button
                                  variant="destructive"
                                  onClick={() => {
                                    deleteProjectMutation.mutate(
                                      { id: project.id },
                                      {
                                        onSuccess: () => {
                                          toast.success(
                                            "Project deleted successfully",
                                          );
                                        },
                                        onError: (error) => {
                                          toast.error(
                                            getErrorMessage(
                                              error,
                                              "Failed to delete project",
                                            ),
                                          );
                                        },
                                      },
                                    );
                                  }}
                                  disabled={deleteProjectMutation.isPending}
                                >
                                  {deleteProjectMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-primary-foreground" />
                                  ) : (
                                    "Delete"
                                  )}
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        )}
                      </SidebarMenuSubItem>
                    ))}
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild className="font-medium">
                        <Link href="/projects">
                          <span>See all </span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>

            <Collapsible
              open={isJamsExpanded}
              onOpenChange={setIsJamsExpanded}
              asChild
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip="History"
                    onClick={(e) => {
                      if (isCollapsed) {
                        e.preventDefault();
                        setIsJamsExpanded(true);
                        toggleSidebar();
                      }
                    }}
                    className="group/history"
                  >
                    <div className="relative flex h-4 w-4 shrink-0 items-center justify-center">
                      {getJamsQuery.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <History
                            className={cn(
                              "absolute inset-0 h-4 w-4 transition-opacity duration-200",
                              !isCollapsed && "group-hover/history:opacity-0",
                            )}
                          />
                          <ChevronRight
                            className={cn(
                              "absolute inset-0 h-4 w-4 transition-all duration-200 group-data-[state=open]/collapsible:rotate-90",
                              isCollapsed
                                ? "scale-0 opacity-0"
                                : "scale-100 opacity-0 group-hover/history:opacity-100",
                            )}
                          />
                        </>
                      )}
                    </div>
                    <span>History</span>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {jams.map((jam) => (
                      <SidebarMenuSubItem
                        key={jam.id}
                        className="group/item relative overflow-hidden"
                      >
                        <SidebarMenuSubButton asChild>
                          <Link
                            href={`/jam/${jam.id}`}
                            className="relative overflow-hidden"
                          >
                            <span
                              className="inline-block max-w-full flex-1 overflow-hidden pr-8 text-nowrap [mask-image:linear-gradient(to_right,black_85%,transparent_100%)] group-hover/item:[mask-image:linear-gradient(to_right,black_70%,transparent_90%)]"
                              style={{
                                textOverflow: "clip",
                              }}
                            >
                              {jam.title || "Untitled Jam"}
                            </span>
                            {jam.status && jam.status !== "idle" && (
                              <div className="size-2 flex-shrink-0 animate-pulse rounded-full bg-primary !opacity-100 transition-opacity duration-200 group-hover/item:!opacity-0" />
                            )}
                          </Link>
                        </SidebarMenuSubButton>
                        {!isCollapsed && (
                          <Dialog>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "absolute top-1/2 right-1 h-6 w-6 -translate-y-1/2 opacity-0 transition-opacity duration-200 group-hover/item:opacity-100",
                                    "flex items-center justify-center hover:bg-accent hover:text-accent-foreground",
                                  )}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent side="right" align="start">
                                <DialogTrigger asChild>
                                  <DropdownMenuItem
                                    className="text-destructive hover:text-destructive"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Trash2 />
                                    Delete
                                  </DropdownMenuItem>
                                </DialogTrigger>
                              </DropdownMenuContent>
                            </DropdownMenu>

                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle>Delete jam</DialogTitle>
                              </DialogHeader>
                              <p className="text-sm text-muted-foreground">
                                Are you sure you want to delete{" "}
                                <span className="font-semibold text-muted">
                                  {jam.title || "Untitled Jam"}
                                </span>
                                ? This action cannot be undone.
                              </p>
                              <DialogFooter>
                                <DialogClose asChild>
                                  <Button
                                    variant="outline"
                                    className="text-foreground"
                                  >
                                    Cancel
                                  </Button>
                                </DialogClose>
                                <Button
                                  variant="destructive"
                                  onClick={() => {
                                    deleteJamMutation.mutate(
                                      { id: jam.id },
                                      {
                                        onSuccess: () => {
                                          toast.success(
                                            "Jam deleted successfully",
                                          );
                                        },
                                        onError: (error) => {
                                          toast.error(
                                            getErrorMessage(
                                              error,
                                              "Failed to delete jam",
                                            ),
                                          );
                                        },
                                      },
                                    );
                                  }}
                                  disabled={deleteJamMutation.isPending}
                                >
                                  {deleteJamMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-primary-foreground" />
                                  ) : (
                                    "Delete"
                                  )}
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        )}
                      </SidebarMenuSubItem>
                    ))}
                    {(hasMoreJams || jams.length === 0) && (
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          onClick={() => {
                            setIsSearchDialogOpen(true);
                          }}
                          className="font-medium"
                        >
                          <span>{hasMoreJams ? "Show more" : "See all"}</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    )}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <Dialog open={isCreateProjectOpen} onOpenChange={setIsCreateProjectOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Create new project</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-6 py-6">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name">
                <span>
                  Project name <span className="text-destructive">*</span>
                </span>
              </Label>
              <Input
                id="name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="E.g. Capybara Research"
                className="bg-muted-foreground/20"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
                placeholder="What is this project about?"
                className="bg-muted-foreground/20"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="instructions">
                <span>
                  Instructions{" "}
                  <span className="text-muted-foreground">
                    (tailor the way Scout responds in this project)
                  </span>
                </span>
              </Label>
              <Textarea
                id="instructions"
                value={newProjectInstructions}
                onChange={(e) => setNewProjectInstructions(e.target.value)}
                placeholder="E.g. Always include a capybara image in the output"
                rows={4}
                className="resize-none border-none bg-muted-foreground/20 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateProjectOpen(false)}
              className="text-foreground"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={createProjectMutation.isPending}
            >
              {createProjectMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-primary-foreground" />
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SidebarFooter>
        <div
          className={cn(
            "items-center gap-2 p-1 !pb-[env(safe-area-inset-bottom)] sm:!pb-1",
            isCollapsed ? "flex flex-col" : "flex flex-row justify-between",
          )}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "relative h-8 w-8 overflow-hidden rounded-full p-0",
                )}
              >
                <div className="relative size-8 flex-shrink-0 overflow-hidden rounded-full">
                  {user ? (
                    <Image
                      src={user.imageUrl}
                      alt={user.fullName || "Profile"}
                      fill
                      className="object-cover"
                    />
                  ) : (
                    <div className="h-full w-full animate-pulse bg-muted" />
                  )}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side="top"
              sideOffset={8}
              className="w-56"
            >
              <DropdownMenuItem
                onClick={() => {
                  if (plan?.id === "pro") {
                    setIsGetExtraDialogOpen(true);
                  } else {
                    setIsUpgradeDialogOpen(true);
                  }
                }}
              >
                <Sparkles />
                {plan?.id === "pro" ? "Get extra hours" : "Upgrade"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setIsSettingsDialogOpen(true)}>
                <Settings />
                Settings
              </DropdownMenuItem>
              <a href="https://discord.gg/hhCUHjRSGA" target="_blank">
                <DropdownMenuItem>
                  <MessageSquareWarning />
                  Help & feedback
                </DropdownMenuItem>
              </a>
              <DropdownMenuItem
                className="text-destructive hover:text-destructive"
                onClick={() => {
                  queryClient.clear();
                  signOut();
                }}
              >
                <LogOut />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className={cn(
              "size-9 text-sidebar-foreground hover:text-sidebar-accent-foreground",
              isCollapsed && "self-center",
            )}
          >
            {isCollapsed ? <PanelRightClose /> : <PanelRightOpen />}
            <span className="sr-only">Toggle sidebar</span>
          </Button>
        </div>
      </SidebarFooter>
      <SidebarRail />
    </>
  );
}

export default function AppSidebar({
  initialAuthState,
}: {
  initialAuthState?: boolean;
}) {
  const pathname = usePathname();
  const { user, isSignedIn, isLoaded } = useUser();
  const { setIsSearchDialogOpen, isSearchDialogOpen } = useSidebarStore();
  const { scrollY } = useScroll();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isLogoPlaying, setIsLogoPlaying] = useState(false);
  const [shouldResetLogo, setShouldResetLogo] = useState(false);

  const getPlanQuery = usePlanQuery();
  const plan = getPlanQuery.data ? PLANS[getPlanQuery.data as Plan] : null;
  const planName = plan?.name ?? "Free";

  useEffect(() => {
    if (user) {
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
        plan: planName,
      });
    }
  }, [user, planName]);

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 10);
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsSearchDialogOpen(!isSearchDialogOpen);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setIsSearchDialogOpen, isSearchDialogOpen]);

  if (pathname && pathname.includes("/debug")) {
    return null;
  }

  const effectiveIsSignedIn = isLoaded ? isSignedIn : initialAuthState;

  // If Clerk hasn't loaded and we don't have an initial state, return null
  if (!isLoaded && initialAuthState === undefined) {
    return null;
  }

  if (
    (!effectiveIsSignedIn &&
      (pathname.includes("/jam") || pathname.includes("/share"))) ||
    pathname.includes("/sign-in") ||
    pathname.includes("/sign-up") ||
    pathname.includes("/dev-auth")
  ) {
    return null;
  }

  // Show signed out header when user is not signed in
  if (!effectiveIsSignedIn) {
    return (
      <header
        className={cn(
          "fixed top-0 right-0 left-0 z-50 flex items-center justify-between border-b bg-background p-2 transition-[border]",
          isScrolled ? "border-border" : "border-transparent",
        )}
      >
        <Link
          href="/"
          className="flex items-center justify-center p-2"
          onMouseEnter={() => {
            setIsLogoPlaying(true);
            setShouldResetLogo(false);
          }}
          onMouseLeave={() => {
            setIsLogoPlaying(false);
            setShouldResetLogo(true);
          }}
        >
          <Lottie
            className="size-6"
            src="https://cdn.lottielab.com/l/6H7EEEBrHF5oR7.json"
            playing={isLogoPlaying}
            {...(shouldResetLogo && { time: 0 })}
          />
        </Link>
        <div className="flex items-center gap-2">
          <Link href="/sign-up">
            <Button>Sign up</Button>
          </Link>
          <Link href="/sign-in">
            <Button variant="secondary">Sign in</Button>
          </Link>
        </div>
      </header>
    );
  }

  // Show the sidebar for signed-in users
  // This will render immediately based on the cookie, preventing layout shift
  return (
    <>
      <Sidebar collapsible="icon" className="border-r">
        <SidebarContent_ />
      </Sidebar>

      <SearchDialog />
      <SettingsDialog />
      <UpgradeDialog />
      <GetExtraDialog />
      <ConnectDialog />
      <SidebarTrigger className="fixed top-2 left-2 z-40 size-9 text-foreground/70 md:hidden" />
    </>
  );
}
